import { useCallback, useEffect, useRef, type RefObject } from 'react';
import type { DragStart } from '@hello-pangea/dnd';

type UseDragAutoScrollParams = {
    scrollContainerRef: RefObject<HTMLDivElement | null>;
};

const EDGE_THRESHOLD_PX = 112;
const MAX_SCROLL_STEP_PX = 32;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const easeScrollSpeed = (distanceToEdge: number) => {
    const proximity = 1 - clamp(distanceToEdge / EDGE_THRESHOLD_PX, 0, 1);
    return Math.ceil(proximity * proximity * MAX_SCROLL_STEP_PX);
};

const isTestBlockDrag = ({ source, draggableId }: DragStart) =>
    (source.droppableId === 'toolbox' && draggableId.startsWith('tool_')) ||
    source.droppableId.startsWith('test_blocks_');

export function useDragAutoScroll({ scrollContainerRef }: UseDragAutoScrollParams) {
    const isActiveRef = useRef(false);
    const pointerYRef = useRef<number | null>(null);
    const animationFrameRef = useRef<number | null>(null);

    const stopAutoScroll = useCallback(() => {
        isActiveRef.current = false;
        pointerYRef.current = null;

        if (animationFrameRef.current !== null) {
            window.cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }

        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('touchmove', handleTouchMove);
    }, []);

    const tick = useCallback(() => {
        if (!isActiveRef.current) {
            animationFrameRef.current = null;
            return;
        }

        const container = scrollContainerRef.current;
        const pointerY = pointerYRef.current;

        if (!container || pointerY === null) {
            animationFrameRef.current = window.requestAnimationFrame(tick);
            return;
        }

        const rect = container.getBoundingClientRect();
        const topDistance = pointerY - rect.top;
        const bottomDistance = rect.bottom - pointerY;
        let delta = 0;

        if (topDistance >= 0 && topDistance <= EDGE_THRESHOLD_PX) {
            delta = -easeScrollSpeed(topDistance);
        } else if (bottomDistance >= 0 && bottomDistance <= EDGE_THRESHOLD_PX) {
            delta = easeScrollSpeed(bottomDistance);
        }

        if (delta !== 0) {
            // 'instant' bypasses the container's scroll-smooth, which would
            // otherwise re-animate every frame and crawl
            container.scrollBy({ top: delta, behavior: 'instant' });
        }

        animationFrameRef.current = window.requestAnimationFrame(tick);
    }, [scrollContainerRef]);

    function handleMouseMove(event: MouseEvent) {
        pointerYRef.current = event.clientY;
    }

    function handleTouchMove(event: TouchEvent) {
        const touch = event.touches[0];
        pointerYRef.current = touch ? touch.clientY : null;
    }

    const startAutoScroll = useCallback(
        (dragStart: DragStart) => {
            stopAutoScroll();

            if (!isTestBlockDrag(dragStart)) {
                return;
            }

            isActiveRef.current = true;
            window.addEventListener('mousemove', handleMouseMove, {
                passive: true,
            });
            window.addEventListener('touchmove', handleTouchMove, {
                passive: true,
            });
            animationFrameRef.current = window.requestAnimationFrame(tick);
        },
        [stopAutoScroll, tick],
    );

    useEffect(() => stopAutoScroll, [stopAutoScroll]);

    return {
        startAutoScroll,
        stopAutoScroll,
    };
}
